'use client';

import { ChevronRight, Delete, Remove } from '@crabs-ai/web-icons';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ControlsHeader,
  Label,
  MenuItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SelectFieldTrigger,
  Slider,
  ToolButton,
  ValueInputField,
} from '@crabs-ai/ds-react';
import AnimationPreview from '../../editor-property-panel/ui/contents/animation/AnimationPreview';
import { CollapsibleContent } from '../collapsible-content';
import { useInspectorSections } from '../use-inspector-sections';
import { AnimationAddPopover } from './animation-add-popover';
import { PositionKeyframeControls } from './position-keyframe-controls';
import { ZoomKeyframeControls } from './zoom-keyframe-controls';
import { useAnimationAddEnabled } from './animation-add-enabled-context';
import { getCanCrop } from '../../utils/get-crop-from-item';
import { useFps, useItem, useWriteContext } from '../../utils/use-context';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { EditorStarterItem } from '../../items/item-type';
import {
  ANIMATION_EASING_KEYS,
  ANIMATION_META,
  AnimationEasingKey,
  AnimationProperty,
  AnimationType,
  getDefaultEasingKey,
  ItemAnimation,
} from '../../items/animation/animation-item-type';
import {
  ANIMATION_PROPERTY_INPUT,
  getEditableProperties,
  getKeyframeLabelKeys,
  resolveEditableOutputRange,
  setOutputRangeValue,
} from '../../items/animation/animation-output-range';
import { useTranslations } from 'next-intl';

interface AnimationControlsProps {
  itemId: string;
  animations: ItemAnimation[];
  durationInFrames: number;
  collapseAllTrigger?: number;
  expandAllTrigger?: number;
}

// 섹션 기본 상태 — 고급 설정(키프레임 묶음)만 기본 접힘, 나머지는 펼침.
const SECTION_DEFAULTS = {
  animation: true,
  advancedSettings: false,
  positionKeyframes: true,
  zoomKeyframes: true,
};

// 최소 Animation duration (0.1초)
const MIN_ANIMATION_DURATION_IN_SECONDS = 0.1;

// 애니 카드 헤더 — 펼침 토글 + 이름·카테고리 + 삭제. 활성 체크박스는 없다 —
// 애니는 넣거나 빼거나 둘 중 하나(끄기 상태 없음).
interface AnimationCardHeaderProps {
  animation: ItemAnimation;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onRemove: () => void;
}

const AnimationCardHeader: React.FC<AnimationCardHeaderProps> = ({
  animation,
  isExpanded,
  onToggleExpand,
  onRemove,
}) => {
  const t = useTranslations();
  const meta = ANIMATION_META[animation.type];

  // 카테고리 라벨
  const categoryLabel = useMemo(() => {
    return t(`Editor.animation.category.${meta.category}`);
  }, [meta.category, t]);

  // 카테고리별 색상: in/out은 neutral, combo는 primary
  const categoryColor = meta.category === 'combo' ? 'primary' : 'neutral';

  return (
    <div className="flex items-center gap-2xs py-2xs">
      {/* 이름·카테고리 전체가 펼침 토글 — 클릭 타깃을 넓게 준다 */}
      <button
        type="button"
        onClick={onToggleExpand}
        aria-expanded={isExpanded}
        className="flex min-w-0 flex-1 items-center gap-2xs text-left focus-visible:outline-none"
      >
        <ChevronRight
          className={`size-4 shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
        />
        <span className="truncate text-comp-moderate-medium text-sys-cont-neutral-default">
          {t(`Editor.animation.type.${animation.type}`)}
        </span>
        <Label
          size="small"
          color={categoryColor}
        >
          {categoryLabel}
        </Label>
      </button>

      <ToolButton onClick={onRemove}>
        <Remove />
      </ToolButton>
    </div>
  );
};

// 프리뷰 반복 재생 — 1초 재생 + 0.4초 멈춤을 반복한다. 접기 애니메이션 때문에 접힌 카드도
// 마운트는 유지되므로, rAF 는 active(펼침)일 때만 돌린다(rAF 절약).
const PREVIEW_PLAY_MS = 1000;
const PREVIEW_CYCLE_MS = 1400;

const AnimationLoopPreview: React.FC<{
  animation: ItemAnimation;
  active: boolean;
}> = ({ animation, active }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!active) {
      setProgress(0);
      return;
    }

    let raf = 0;
    let startedAt: number | null = null;

    const tick = (timestamp: number) => {
      if (startedAt === null) startedAt = timestamp;
      const elapsed = (timestamp - startedAt) % PREVIEW_CYCLE_MS;
      setProgress(Math.min(elapsed / PREVIEW_PLAY_MS, 1));
      raf = window.requestAnimationFrame(tick);
    };

    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [active]);

  return (
    <div className="aspect-[16/9] w-full">
      <AnimationPreview
        type={animation.type}
        progress={progress}
        animation={animation}
      />
    </div>
  );
};

// property 하나의 곡선 드롭다운 — 값이 아니라 "그 값까지 어떻게 가는가".
// popIn 처럼 크기 + 투명도를 같이 움직이는 애니는 축마다 곡선이 달라야 한다(크기=튕김, 투명도=감속).
const AnimationEasingSelect: React.FC<{
  animation: ItemAnimation;
  property: AnimationProperty;
  onChange: (property: AnimationProperty, easing: AnimationEasingKey) => void;
}> = ({ animation, property, onChange }) => {
  const t = useTranslations();

  // 사용자가 고르기 전에는 타입 기본 곡선이 걸려 있다 — 그걸 그대로 표시해 빈칸이 안 되게 한다.
  // 메타가 프리셋 밖 함수(엔진 기본값)를 쓰면 "기본".
  const currentKey =
    animation.easings?.[property] ??
    getDefaultEasingKey(animation.type, property);
  const currentLabel = currentKey
    ? t(`Editor.animation.easing.${currentKey}`)
    : t('Editor.animation.easing.default');

  return (
    <Popover>
      <PopoverTrigger asChild>
        <SelectFieldTrigger value={currentLabel} />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-[120px] rounded-xs border border-sys-style-neutral-default bg-sys-bg-surface-1 px-3xs py-2xs shadow-[0px_0px_16px_0px_rgba(4,4,5,0.08)]"
      >
        {ANIMATION_EASING_KEYS.map((key) => (
          <MenuItem
            key={key}
            size="md"
            text={t(`Editor.animation.easing.${key}`)}
            singleSelectType
            className="w-full rounded-xs"
            isSelected={currentKey === key}
            onClick={() => onChange(property, key)}
          />
        ))}
      </PopoverContent>
    </Popover>
  );
};

// 애니메이션 길이 조절 아이템 컴포넌트
interface AnimationDurationItemProps {
  itemId: string;
  animation: ItemAnimation;
  durationInFrames: number;
  targetIds: string[];
  fps: number;
  // In/Out 애니메이션들의 상태 (서로 영향을 주기 위함)
  allAnimations: ItemAnimation[];
}

const AnimationDurationItem: React.FC<AnimationDurationItemProps> = ({
  itemId,
  animation,
  durationInFrames,
  targetIds,
  fps,
  allAnimations,
}) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const meta = ANIMATION_META[animation.type];

  // 아이템 duration을 초 단위로 변환
  const itemDurationInSeconds = durationInFrames / fps;

  // In/Out 애니메이션 찾기
  const inAnimation = allAnimations.find(
    (a) => ANIMATION_META[a.type].category === 'in',
  );
  const outAnimation = allAnimations.find(
    (a) => ANIMATION_META[a.type].category === 'out',
  );

  // In/Out의 경우 서로 영향을 받음: 최대 duration은 아이템 길이 - 상대방 duration
  const getMaxDuration = useCallback(() => {
    if (meta.category === 'in' && outAnimation) {
      return Math.max(
        MIN_ANIMATION_DURATION_IN_SECONDS,
        itemDurationInSeconds - outAnimation.durationInSeconds,
      );
    }
    if (meta.category === 'out' && inAnimation) {
      return Math.max(
        MIN_ANIMATION_DURATION_IN_SECONDS,
        itemDurationInSeconds - inAnimation.durationInSeconds,
      );
    }
    // Combo나 단독 In/Out의 경우 아이템 전체 길이
    return itemDurationInSeconds;
  }, [meta.category, inAnimation, outAnimation, itemDurationInSeconds]);

  const maxDuration = getMaxDuration();
  const minDuration = MIN_ANIMATION_DURATION_IN_SECONDS;

  // Animation duration 변경 (In/Out용)
  const handleDurationChange = useCallback(
    (newDuration: number, commitToUndoStack: boolean) => {
      const clampedDuration = Math.max(
        minDuration,
        Math.min(maxDuration, newDuration),
      );
      setState({
        groupPropertyEdit: 'animation',
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (item) => {
            const prev = item as EditorStarterItem & {
              animations?: ItemAnimation[];
            };
            if (!prev.animations) return prev;
            return {
              ...prev,
              animations: prev.animations.map((e) =>
                e.type === animation.type
                  ? { ...e, durationInSeconds: clampedDuration, timelineWindow:undefined }
                  : e,
              ),
            };
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds, maxDuration, minDuration, animation.type],
  );

  // Combo용: 시작 시간 변경
  const handleStartTimeChange = useCallback(
    (newStart: number, commitToUndoStack: boolean) => {
      setState({
        groupPropertyEdit: 'animation',
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (item) => {
            const prev = item as EditorStarterItem & {
              animations?: ItemAnimation[];
            };
            if (!prev.animations) return prev;
            return {
              ...prev,
              animations: prev.animations.map((e) =>
                e.type === animation.type
                  ? { ...e, startTimeInSeconds: newStart, timelineWindow:undefined }
                  : e,
              ),
            };
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds, animation.type],
  );

  // Combo용: 끝 시간 변경
  const handleEndTimeChange = useCallback(
    (newEnd: number, commitToUndoStack: boolean) => {
      setState({
        groupPropertyEdit: 'animation',
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (item) => {
            const prev = item as EditorStarterItem & {
              animations?: ItemAnimation[];
            };
            if (!prev.animations) return prev;
            return {
              ...prev,
              animations: prev.animations.map((e) =>
                e.type === animation.type
                  ? { ...e, endTimeInSeconds: newEnd, timelineWindow:undefined }
                  : e,
              ),
            };
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds, animation.type],
  );

  const isCombo = meta.category === 'combo';

  // Combo의 시작/끝 시간 (기본값 설정)
  const startTime = animation.startTimeInSeconds ?? 0;
  const endTime = animation.endTimeInSeconds ?? itemDurationInSeconds;

  // 최소 간격
  const RANGE_STEP = 0.1;

  // Start 값 변경 (end를 넘지 않도록 제약)
  const handleConstrainedStartChange = useCallback(
    (value: number, commitToUndoStack: boolean) => {
      const maxStart = endTime - RANGE_STEP;
      const constrainedValue = Math.min(Math.max(0, value), maxStart);
      handleStartTimeChange(constrainedValue, commitToUndoStack);
    },
    [endTime, handleStartTimeChange],
  );

  // End 값 변경 (start보다 작아지지 않도록 제약)
  const handleConstrainedEndChange = useCallback(
    (value: number, commitToUndoStack: boolean) => {
      const minEnd = startTime + RANGE_STEP;
      const constrainedValue = Math.max(
        minEnd,
        Math.min(value, itemDurationInSeconds),
      );
      handleEndTimeChange(constrainedValue, commitToUndoStack);
    },
    [startTime, itemDurationInSeconds, handleEndTimeChange],
  );

  // Range Slider 값 변경 핸들러
  const handleRangeChange = useCallback(
    (values: number[], commitToUndoStack: boolean) => {
      const newStart = values[0] ?? 0;
      const newEnd = values[1] ?? itemDurationInSeconds;

      // start와 end가 최소 간격을 유지하도록 제약
      if (newStart <= newEnd - RANGE_STEP) {
        handleStartTimeChange(newStart, commitToUndoStack);
        handleEndTimeChange(newEnd, commitToUndoStack);
      }
    },
    [handleStartTimeChange, handleEndTimeChange, itemDurationInSeconds],
  );

  // 값 행과 같은 한 줄 포맷: [이름] [컨트롤…]. 애니 이름은 카드 헤더에 있으므로 여기선
  // 무엇을 조절하는지(구간/지속 시간)만.
  if (isCombo) {
    return (
      <div className="flex items-center gap-4xs">
        <span className="w-[68px] shrink-0 truncate text-caption-small text-sys-cont-neutral-light">
          {t('Editor.inspector.animation.range')}
        </span>

        <ValueInputField
          value={Number(startTime.toFixed(1))}
          setValue={(update) =>
            handleConstrainedStartChange(update.num, update.commitToUndoStack)
          }
          min={0}
          max={endTime - RANGE_STEP}
          step={RANGE_STEP}
          isUnit="s"
          showStepper={false}
          accessibilityLabel={`${t(`Editor.animation.type.${animation.type}`)} Start`}
          className="w-[56px] shrink-0"
        />

        <Slider
          value={[startTime, endTime]}
          onValueChange={(values) => handleRangeChange(values, false)}
          onValueCommit={(values) => handleRangeChange(values, true)}
          min={0}
          max={itemDurationInSeconds}
          step={RANGE_STEP}
          minStepsBetweenThumbs={1}
          className="min-w-0 flex-1"
          isShowTooltip={false}
          aria-label={t(`Editor.animation.type.${animation.type}`)}
        />

        <ValueInputField
          value={Number(endTime.toFixed(1))}
          setValue={(update) =>
            handleConstrainedEndChange(update.num, update.commitToUndoStack)
          }
          min={startTime + RANGE_STEP}
          max={itemDurationInSeconds}
          step={RANGE_STEP}
          isUnit="s"
          showStepper={false}
          accessibilityLabel={`${t(`Editor.animation.type.${animation.type}`)} End`}
          className="w-[56px] shrink-0"
        />
      </div>
    );
  }

  // In/Out: 단일 슬라이더
  return (
    <div className="flex items-center gap-4xs">
      <span className="w-[68px] shrink-0 truncate text-caption-small text-sys-cont-neutral-light">
        {t('Editor.inspector.animation.duration')}
      </span>

      <Slider
        value={[animation.durationInSeconds]}
        onValueChange={(values) =>
          handleDurationChange(values[0] ?? minDuration, false)
        }
        onValueCommit={(values) =>
          handleDurationChange(values[0] ?? minDuration, true)
        }
        min={minDuration}
        max={maxDuration}
        step={0.1}
        className="min-w-0 flex-1"
        isShowTooltip={false}
        aria-label={`Duration: ${animation.durationInSeconds.toFixed(1)}s`}
      />

      <ValueInputField
        value={Number(animation.durationInSeconds.toFixed(1))}
        setValue={(update) =>
          handleDurationChange(update.num, update.commitToUndoStack)
        }
        min={minDuration}
        max={maxDuration}
        step={0.1}
        isUnit="s"
        showStepper={false}
        accessibilityLabel={t('Editor.inspector.animation.duration')}
        className="w-[56px] shrink-0"
      />
    </div>
  );
};

// 애니 값(시작·중간·끝) 조절 — 그 타입이 쓰는 property 마다 keyframe 값 입력칸.
// 저장은 customOutputRanges 라 렌더 엔진이 바로 읽는다(예: move 의 이동량, zoomIn 의 배율).
interface AnimationValueItemProps {
  itemId: string;
  animation: ItemAnimation;
  targetIds: string[];
}

const AnimationValueItem: React.FC<AnimationValueItemProps> = ({
  itemId,
  animation,
  targetIds,
}) => {
  const t = useTranslations();
  const { setState } = useWriteContext();

  const handleValueChange = useCallback(
    (
      property: AnimationProperty,
      index: number,
      keyframeCount: number,
      value: number,
      commitToUndoStack: boolean,
    ) => {
      setState({
        groupPropertyEdit: 'animation',
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (item) => {
            const prev = item as EditorStarterItem & {
              animations?: ItemAnimation[];
            };
            if (!prev.animations) return prev;
            return {
              ...prev,
              animations: prev.animations.map((e) =>
                e.type === animation.type
                  ? // keyframe 수가 다른 아이템은 같은 index 가 다른 의미라 건너뛴다(다중 선택).
                    setOutputRangeValue(
                      e,
                      property,
                      index,
                      value,
                      keyframeCount,
                    )
                  : e,
              ),
            };
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds, animation.type],
  );

  const handleEasingChange = useCallback(
    (property: AnimationProperty, easing: AnimationEasingKey) => {
      setState({
        groupPropertyEdit: 'animation',
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (item) => {
            const prev = item as EditorStarterItem & {
              animations?: ItemAnimation[];
            };
            if (!prev.animations) return prev;
            return {
              ...prev,
              animations: prev.animations.map((e) =>
                e.type === animation.type
                  ? { ...e, easings: { ...e.easings, [property]: easing } }
                  : e,
              ),
            };
          });
        },
        commitToUndoStack: true,
      });
    },
    [setState, itemId, targetIds, animation.type],
  );

  const keyframeLabel = (key: string): string =>
    key === 'start' || key === 'middle' || key === 'end'
      ? t(`Editor.inspector.animation.${key}`)
      : key;

  return (
    <div className="flex flex-col gap-4xs">
      {getEditableProperties(animation).map((propAnim) => {
        const values = resolveEditableOutputRange(animation, propAnim);
        if (values.length === 0) return null;

        const labels = getKeyframeLabelKeys(values.length);
        const input = ANIMATION_PROPERTY_INPUT[propAnim.property];
        const propertyLabel = t(
          `Editor.animation.property.${propAnim.property}`,
        );

        // 한 줄: [property 이름] [시작] [끝] [곡선 ▾]. keyframe 텍스트 라벨(시작/끝)은 자리를
        // 너무 먹어 빼고 접근성 라벨로만 남긴다 — 순서가 곧 시작→끝이라 눈으로 헷갈릴 게 없다.
        return (
          <div
            key={propAnim.property}
            className="flex items-center gap-4xs"
          >
            <span className="w-[68px] shrink-0 truncate text-caption-small text-sys-cont-neutral-light">
              {propertyLabel}
            </span>

            {values.map((value, index) => (
              <ValueInputField
                key={`${propAnim.property}-${index}`}
                value={value}
                setValue={(update) =>
                  handleValueChange(
                    propAnim.property,
                    index,
                    values.length,
                    update.num,
                    update.commitToUndoStack,
                  )
                }
                min={input.min}
                max={input.max}
                step={input.step}
                isUnit={input.unit}
                showStepper={false}
                accessibilityLabel={`${propertyLabel} ${keyframeLabel(labels[index] ?? `${index + 1}`)}`}
                className="w-[56px] shrink-0"
              />
            ))}

            {/* 끝 값 다음에 그 축의 곡선 — 값(어디까지)과 곡선(어떻게)이 한 줄에 붙는다. */}
            <div className="min-w-0 flex-1">
              <AnimationEasingSelect
                animation={animation}
                property={propAnim.property}
                onChange={handleEasingChange}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AnimationControlsUnmemoized: React.FC<AnimationControlsProps> = ({
  itemId,
  animations,
  durationInFrames,
  collapseAllTrigger = 0,
  expandAllTrigger = 0,
}) => {
  const t = useTranslations();
  const { fps } = useFps();
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);
  const isAddEnabled = useAnimationAddEnabled();
  const item = useItem(itemId);

  // 섹션 접기 상태 — 캐시 키에 suffix 를 붙여 같은 itemId 를 쓰는 타입 인스펙터의
  // useInspectorSections 캐시 엔트리와 충돌하지 않게 한다.
  const { openSections, toggleSection } = useInspectorSections({
    itemId: `${itemId}:animation`,
    defaults: SECTION_DEFAULTS,
    collapseAllTrigger,
    expandAllTrigger,
  });

  // 모든 Animation 삭제 — 위치 키프레임은 별도 필드라 자연히 보존된다.
  const handleRemoveAllAnimations = useCallback(() => {
    setState({
      groupPropertyEdit: 'animation',
      update: (state) => {
        return changeItemsWithSelection(state, itemId, targetIds, (item) => {
          const prev = item as EditorStarterItem & {
            animations?: ItemAnimation[];
          };
          if (!prev.animations) return prev;
          // animations 속성 제거
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { animations: _, ...rest } = prev;
          return rest as EditorStarterItem;
        });
      },
      commitToUndoStack: true,
    });
  }, [setState, itemId, targetIds]);

  // 개별 Animation 삭제
  const handleRemoveAnimation = useCallback(
    (animationType: string) => {
      setState({
        groupPropertyEdit: 'animation',
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (item) => {
            const prev = item as EditorStarterItem & {
              animations?: ItemAnimation[];
            };
            if (!prev.animations) return prev;

            const filtered = prev.animations.filter(
              (e) => e.type !== animationType,
            );

            // animations가 비어있으면 속성 제거
            if (filtered.length === 0) {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { animations: _, ...rest } = prev;
              return rest as EditorStarterItem;
            }

            return {
              ...prev,
              animations: filtered,
            };
          });
        },
        commitToUndoStack: true,
      });
    },
    [setState, itemId, targetIds],
  );

  // 폐기 타입(구 move 등)이 저장 데이터에 남아 있으면 카드 렌더에서 제외(크래시 방지).
  const cardAnimations = useMemo(
    () => animations.filter((a) => a.type in ANIMATION_META),
    [animations],
  );

  // 펼친 카드 하나. 기본은 전부 접힘 — 사용자가 클릭한 카드만 펼친다.
  const [expandedType, setExpandedType] = useState<AnimationType | null>(null);
  const activeType =
    expandedType !== null && cardAnimations.some((a) => a.type === expandedType)
      ? expandedType
      : null;

  return (
    <div className="flex flex-col px-2xs pb-2xs">
      {/* 섹션 헤더 — 클릭으로 접기/펼치기, 🗑 은 arrow 좌측 trailing */}
      <ControlsHeader
        size="sm"
        title={t('Editor.inspector.animation.title')}
        isActive
        isOpened={openSections.animation}
        onClick={() => toggleSection('animation')}
        activeTrailingNode={
          cardAnimations.length > 0 ? (
            <ToolButton
              size="sm"
              onClick={handleRemoveAllAnimations}
              aria-label={t('Editor.inspector.animation.removeAll')}
              isShowTooltip
              tooltipNode={t('Editor.inspector.animation.removeAll')}
            >
              <Delete />
            </ToolButton>
          ) : undefined
        }
      />

      <CollapsibleContent isOpen={openSections.animation}>
        {/* 애니 카드 리스트 — 카드 하나 = 애니 하나(헤더 + 펼치면 프리뷰·값·시간) */}
        <div className="flex flex-col gap-2xs px-md pb-2xs">
          {cardAnimations.length === 0 && (
            <span className="py-2xs text-caption-small text-sys-cont-neutral-lightest">
              {t('Editor.inspector.animation.empty')}
            </span>
          )}

          {cardAnimations.map((animation) => {
            const isExpanded = animation.type === activeType;
            return (
              <div
                key={animation.type}
                className="flex flex-col rounded-xs border border-sys-style-neutral-light"
              >
                <div className="px-2xs">
                  <AnimationCardHeader
                    animation={animation}
                    isExpanded={isExpanded}
                    onToggleExpand={() =>
                      setExpandedType(isExpanded ? null : animation.type)
                    }
                    onRemove={() => handleRemoveAnimation(animation.type)}
                  />
                </div>

                {/* 접기/펼치기 트랜지션 — 내용은 상시 마운트, 높이만 애니메이션 */}
                <CollapsibleContent isOpen={isExpanded}>
                  {/* 세로 정렬 — 예시(프리뷰)를 위에, 설정을 아래에. */}
                  <div className="flex flex-col gap-2xs px-2xs pb-2xs">
                    <AnimationLoopPreview
                      animation={animation}
                      active={isExpanded}
                    />
                    <AnimationValueItem
                      itemId={itemId}
                      animation={animation}
                      targetIds={targetIds}
                    />
                    <AnimationDurationItem
                      itemId={itemId}
                      animation={animation}
                      durationInFrames={durationInFrames}
                      targetIds={targetIds}
                      fps={fps}
                      allAnimations={animations}
                    />
                    {animation.timelineWindow&&<p className="text-body-small text-sys-cont-neutral-secondary">컷 이전의 효과 타이밍을 유지하고 있어요. 시간을 수정하면 현재 컷 기준으로 다시 설정돼요.</p>}
                  </div>
                </CollapsibleContent>
              </div>
            );
          })}

          {isAddEnabled && <AnimationAddPopover itemId={itemId} />}
        </div>
      </CollapsibleContent>

      {/* 고급 설정 — 키프레임(위치·줌) 묶음. 기본 접힘으로 인스펙터 1차 화면을
          가볍게 유지한다. v1 은 단일 선택 전용(다중 일괄 편집은 프레임 축 충돌 여지) */}
      {targetIds.length === 1 && (
        <div className="border-t border-sys-style-neutral-light">
          <ControlsHeader
            size="sm"
            title={t('Editor.inspector.advanced-settings.title')}
            isActive
            isOpened={openSections.advancedSettings}
            onClick={() => toggleSection('advancedSettings')}
          />
          <CollapsibleContent isOpen={openSections.advancedSettings}>
            {/* 위치 키프레임 — 좌측 인셋으로 고급 설정 하위임을 표시 (§3.1 시각 위계) */}
            <div className="border-t border-sys-style-neutral-light pl-2xs">
              <ControlsHeader
                size="sm"
                title={t('Editor.inspector.position-keyframes.title')}
                isActive
                isOpened={openSections.positionKeyframes}
                onClick={() => toggleSection('positionKeyframes')}
              />
              <CollapsibleContent isOpen={openSections.positionKeyframes}>
                <div className="px-md pb-2xs">
                  <PositionKeyframeControls
                    itemId={itemId}
                    durationInFrames={durationInFrames}
                  />
                </div>
              </CollapsibleContent>
            </div>

            {/* 줌 키프레임 — 프레임 고정 내부 줌(punch-in). croppable 타입 전용 */}
            {item && getCanCrop(item) && (
              <div className="border-t border-sys-style-neutral-light pl-2xs">
                <ControlsHeader
                  size="sm"
                  title={t('Editor.inspector.zoom-keyframes.title')}
                  isActive
                  isOpened={openSections.zoomKeyframes}
                  onClick={() => toggleSection('zoomKeyframes')}
                />
                <CollapsibleContent isOpen={openSections.zoomKeyframes}>
                  <div className="px-md pb-2xs">
                    <ZoomKeyframeControls
                      itemId={itemId}
                      durationInFrames={durationInFrames}
                    />
                  </div>
                </CollapsibleContent>
              </div>
            )}
          </CollapsibleContent>
        </div>
      )}
    </div>
  );
};

export const AnimationControls = memo(AnimationControlsUnmemoized);
